import { fetchGeoLocationsByIds } from '@/api/geoService';
import { getPriceValue } from '@/api/iaziApi';
import { API, fetchPropertyData, getMyProperties } from '@/api/propertyValuation';
import { PriceValueResponse } from '@/models/queries/getPriceValue';
import { PropertyValuationResult } from '@/models/queries/getPropertyData';
import { AppState } from '@/store/types';
import { Pages, routeLocationToGeoIdMapping } from '@/types';
import { captureAction } from '@smg-real-estate/browser-rum';
import { GeoLocation } from '@smg-real-estate/geo-libs';
import { ActionContext, ActionTree, GetterTree, Module, MutationTree } from 'vuex';

export interface CommonModule extends Module<CommonState, AppState> {
  state: () => CommonState;
  getters: CommonGetters;
  mutations: CommonMutations;
  actions: CommonActions;
}

export interface CommonState {
  currentPage: Pages | null;
  isResolvingLocations: boolean;
  routeGeoLocations: GeoLocation[];
  routeLocationsPrice: PriceValueResponse[];
  isUserFirstTimeLogin: boolean;
  loggedIn: boolean;
  username: string;
  howSource: string;
  userProperties: PropertyValuationResult[] | null;
  canUserCreate: boolean;
}
export interface CommonGetters extends GetterTree<CommonState, AppState> {
  getCurrentPage: (state: CommonState) => Pages | null;
  getIsResolvingLocations: (state: CommonState) => boolean;
  getRouteGeoLocations: (state: CommonState) => GeoLocation[];
  getRouteLocationsPrice: (state: CommonState) => PriceValueResponse[];
  getIsUserFirstTimeLogin: (state: CommonState) => boolean;
  getHowSource: (state: CommonState) => string;
  getUserProperties: (state: CommonState) => PropertyValuationResult[] | null;
  canCreateProperty: (state: CommonState) => boolean;
}

export interface CommonMutations extends MutationTree<CommonState> {
  setCurrentPage: (state: CommonState, currentPage: Pages | null) => void;
  setIsResolvingLocations: (state: CommonState, isResolvingLocations: boolean) => void;
  setRouteGeoLocations: (state: CommonState, geoLocations: GeoLocation[]) => void;
  setRouteLocationsPrice: (state: CommonState, routeLocationsPrice: PriceValueResponse[]) => void;
  setIsUserFirstTimeLogin: (state: CommonState, isUserFirstTimeLogin: boolean) => void;
  setHowSource: (state: CommonState, howSource: string) => void;
  setUserProperties: (state: CommonState, userProperties: PropertyValuationResult[] | null) => void;
  setCanUserCreate: (state: CommonState, canUserCreate: boolean) => void;
}

interface CommonActions extends ActionTree<CommonState, AppState> {
  resolveRouteGeoLocations: (state: ActionContext<CommonState, AppState>) => Promise<void>;
  fetchPriceDataForRouteLocations: (state: ActionContext<CommonState, AppState>) => Promise<void>;
  fetchUserProperties: (
    context: ActionContext<CommonState, AppState>,
    forceRefresh?: boolean,
  ) => Promise<PropertyValuationResult[] | null>;
  fetchUserProperty: (
    context: ActionContext<CommonState, AppState>,
    propertyId: string,
  ) => Promise<PropertyValuationResult | null>;
}

export const getDefaultState = (): CommonState => ({
  currentPage: null,
  isResolvingLocations: true,
  routeGeoLocations: [],
  routeLocationsPrice: [],
  isUserFirstTimeLogin: false,
  loggedIn: false,
  username: '',
  howSource: '',
  userProperties: null,
  canUserCreate: true,
});

const state = (): CommonState => getDefaultState();

const getters: CommonGetters = {
  getCurrentPage: (state: CommonState): Pages | null => state.currentPage,
  getIsResolvingLocations: (state: CommonState): boolean => state.isResolvingLocations,
  getRouteGeoLocations: (state: CommonState): GeoLocation[] => state.routeGeoLocations,
  getRouteLocationsPrice: (state: CommonState) => state.routeLocationsPrice,
  getIsUserFirstTimeLogin: (state: CommonState) => state.isUserFirstTimeLogin,
  getIsUserLoggedIn: (state: CommonState) => state.loggedIn,
  getUsername: (state: CommonState) => state.username,
  getHowSource: (state: CommonState): string => state.howSource,
  getUserProperties: (state: CommonState): PropertyValuationResult[] | null => state.userProperties,
  canCreateProperty: (state: CommonState): boolean => state.canUserCreate,
};

const mutations: CommonMutations = {
  setCurrentPage(state: CommonState, currentPage: Pages | null): void {
    state.currentPage = currentPage;
  },
  setIsResolvingLocations(state: CommonState, isResolvingLocations: boolean): void {
    state.isResolvingLocations = isResolvingLocations;
  },
  setRouteGeoLocations(state: CommonState, geoLocations: GeoLocation[]): void {
    state.routeGeoLocations = geoLocations;
  },
  setRouteLocationsPrice(state: CommonState, routeLocationsPrice: PriceValueResponse[]): void {
    state.routeLocationsPrice = routeLocationsPrice;
  },
  setIsUserFirstTimeLogin: (state: CommonState, isUserFirstTimeLogin: boolean) =>
    (state.isUserFirstTimeLogin = isUserFirstTimeLogin),
  setHowSource(state: CommonState, howSource: string): void {
    state.howSource = howSource;
  },
  setLoggedInUser(state: CommonState, username: string): void {
    state.loggedIn = true;
    state.username = username;
  },
  resetUser(state: CommonState): void {
    state.loggedIn = false;
    state.username = '';
    state.howSource = '';
    state.userProperties = null;
    state.canUserCreate = true;
  },
  setUserProperties(state: CommonState, userProperties: PropertyValuationResult[] | null): void {
    state.userProperties = userProperties;
  },
  setCanUserCreate(state: CommonState, canUserCreate: boolean): void {
    state.canUserCreate = canUserCreate;
  },
};

const actions: CommonActions = {
  resolveRouteGeoLocations: async (
    context: ActionContext<CommonState, AppState>,
  ): Promise<void> => {
    const routeLocationIds = Object.values(routeLocationToGeoIdMapping);
    context.commit('setIsResolvingLocations', true);
    const geoLocations = await fetchGeoLocationsByIds(routeLocationIds);
    context.commit('setRouteGeoLocations', geoLocations);
    await context.dispatch('fetchPriceDataForRouteLocations');
    context.commit('setIsResolvingLocations', false);
  },

  fetchPriceDataForRouteLocations: async (
    context: ActionContext<CommonState, AppState>,
  ): Promise<void> => {
    const routeGeoLocations: GeoLocation[] = context.getters.getRouteGeoLocations;
    if (!routeGeoLocations.length) {
      return;
    }
    const result: Array<PriceValueResponse | undefined> = await Promise.all(
      routeGeoLocations.map((geoLocation) => {
        const level = geoLocation.iazi?.level;
        const levelId = geoLocation.iazi?.id;
        if (!level || !levelId) {
          captureAction(`No iazi level and id found for ${geoLocation.id}`, {
            geoId: geoLocation.id,
          });
          return;
        }
        return getPriceValue({ level, levelId });
      }),
    );
    context.commit(
      'setRouteLocationsPrice',
      result.filter((result) => Boolean(result)),
    );
  },

  /**
   * Fetches user properties and caches them in the store.
   * If properties already exist in store and forceRefresh is false, returns cached data.
   * @param forceRefresh - If true, bypasses cache and fetches fresh data
   * @returns Array of user properties or null if fetch fails
   */
  fetchUserProperties: async (
    context: ActionContext<CommonState, AppState>,
    forceRefresh = false,
  ): Promise<PropertyValuationResult[] | null> => {
    const cachedProperties = context.getters.getUserProperties;
    if (cachedProperties && !forceRefresh) {
      return cachedProperties;
    }

    const response = await getMyProperties();
    const properties = response?.data || null;

    context.commit('setUserProperties', properties);

    if (response) {
      context.commit('setCanUserCreate', response.canUserCreate ?? true);
    }

    return properties;
  },

  /**
   * Fetches a single user property by ID, checking cache first.
   * If property exists in cache, returns cached version.
   * If not in cache, fetches from API without updating the cache.
   * @param propertyId - The ID of the property to fetch
   * @returns Single property or null if not found/fetch fails
   */
  fetchUserProperty: async (
    context: ActionContext<CommonState, AppState>,
    propertyId: string,
  ): Promise<PropertyValuationResult | null> => {
    // Check cache first
    const cachedProperties = context.getters.getUserProperties;
    if (cachedProperties) {
      const cachedProperty = cachedProperties.find((prop) => prop.leadId === propertyId);
      if (cachedProperty) {
        return cachedProperty;
      }
    }

    // Not in cache, fetch from API
    try {
      const propertyData = await fetchPropertyData(propertyId, 4, API.HomeOwnerWorld);
      return propertyData ? (propertyData as PropertyValuationResult) : null;
    } catch (error) {
      captureAction('Failed to fetch user property', { propertyId, error });
      return null;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
} as CommonModule;
